import React from "react";
import { Autocomplete, Box, Grid, MenuItem, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  LABEL_ANALYSIS_BOOLEAN_FILTERS,
  LABEL_ANALYSIS_TRI_STATE_FILTER_CHOICES,
  formatCountryOriginLabel,
} from "../../../services/labelAnalysis";

const toOption = (value, label) => ({ value, label: label || value });

function FilterAutocomplete({ label, value, onChange, options }) {
  const selected = options.find((option) => option.value === value) || null;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={options}
      value={selected}
      onChange={(_, option) => onChange(option?.value || "")}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(option, next) => option?.value === next?.value}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}

function LocationLabelToolbar({
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  countryOrigin,
  onCountryOriginChange,
  countryOptions = [],
  grade,
  onGradeChange,
  gradeOptions = [],
  quality,
  onQualityChange,
  qualityOptions = [],
  product,
  onProductChange,
  productOptions = [],
  retailer,
  onRetailerChange,
  retailerOptions = [],
  booleanFilters = {},
  onBooleanFilterChange,
}) {
  const countryChoices = countryOptions.map((code) => toOption(code, formatCountryOriginLabel(code)));
  const gradeChoices = gradeOptions.map((value) => toOption(value));
  const qualityChoices = qualityOptions.map((value) => toOption(value));
  const productChoices = productOptions.map((value) => toOption(value));
  const retailerChoices = retailerOptions.map((value) => toOption(value));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="From"
              value={dateFrom}
              onChange={onDateFromChange}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="To"
              value={dateTo}
              onChange={onDateToChange}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FilterAutocomplete
              label="Product"
              value={product}
              onChange={onProductChange}
              options={productChoices}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FilterAutocomplete
              label="Retailer"
              value={retailer}
              onChange={onRetailerChange}
              options={retailerChoices}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FilterAutocomplete
              label="Country"
              value={countryOrigin}
              onChange={onCountryOriginChange}
              options={countryChoices}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FilterAutocomplete
              label="Grade"
              value={grade}
              onChange={onGradeChange}
              options={gradeChoices}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FilterAutocomplete
              label="Quality"
              value={quality}
              onChange={onQualityChange}
              options={qualityChoices}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {LABEL_ANALYSIS_BOOLEAN_FILTERS.map((filter) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={filter.key}>
              <TextField
                select
                fullWidth
                size="small"
                label={filter.label}
                value={booleanFilters[filter.key] || "all"}
                onChange={(event) => onBooleanFilterChange(filter.key, event.target.value)}
              >
                {LABEL_ANALYSIS_TRI_STATE_FILTER_CHOICES.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

export default LocationLabelToolbar;
